import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';
import { accountService } from '../services/api';
import { storefrontTypeFromApiAccount } from '../utils/adminPaymentAccountMethod';
import { pickVisibleAccountNumber } from '../utils/paymentAccountDisplay';
import { accountsArrayFromResponse } from '../utils/paymentAccountsResponse';

/** Backend no longer accepts `safepay` on orders — skip legacy SafePay accounts from checkout */
const isUnsupportedPaymentAccount = (account) =>
  String(account?.paymentMethod || '').toLowerCase() === 'safepay';

function normalizeAccountDetails(raw) {
  let accountDetails = raw ?? {};
  let guard = 0;
  while (typeof accountDetails === 'string' && guard++ < 4) {
    try {
      accountDetails = JSON.parse(accountDetails);
    } catch {
      accountDetails = {};
      break;
    }
  }
  if (!accountDetails || typeof accountDetails !== 'object') accountDetails = {};
  return accountDetails;
}

/** WhatsApp for payment proof: camelCase / snake_case; nested accountDetails or flat; JSON string details. */
function pickPaymentProofWhatsApp(account) {
  if (!account || typeof account !== 'object') return '';
  const det = normalizeAccountDetails(account.accountDetails);
  return String(
    det.paymentProofWhatsApp ||
      det.payment_proof_whatsapp ||
      account.paymentProofWhatsApp ||
      account.payment_proof_whatsapp ||
      ''
  ).trim();
}

function buildCheckoutMethods(list) {
  const transformedMethods = list
    .filter(
      (account) =>
        account.isActive && account.isVisible && !isUnsupportedPaymentAccount(account)
    )
    .map((account) => {
      const accountDetails = normalizeAccountDetails(account.accountDetails);
      const accountNumber = pickVisibleAccountNumber(account, accountDetails);
      const proofWa = pickPaymentProofWhatsApp(account);

      return {
        id: account._id || account.id,
        name: account.name,
        type: storefrontTypeFromApiAccount(account),
        icon: `fas fa-${account.icon || 'wallet'}`,
        isActive: account.isActive,
        isVisible: account.isVisible,
        description: account.description || '',
        accountNumber,
        accountTitle:
          accountDetails.accountTitle ||
          account.accountHolderName ||
          account.accountTitle ||
          accountDetails.accountHolderName ||
          '',
        bankName: accountDetails.bankName || account.bankName || '',
        paymentProofWhatsApp: proofWa,
        displayOrder: account.displayOrder || 999,
      };
    })
    .sort((a, b) => (a.displayOrder || 999) - (b.displayOrder || 999));

  return transformedMethods;
}

const PaymentContext = createContext();

export const usePayment = () => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error('usePayment must be used within a PaymentProvider');
  }
  return context;
};

export const PaymentProvider = ({ children }) => {
  // Payment methods from backend API
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);

  // Fetch visible payment accounts from backend
  useEffect(() => {
    const fetchPaymentAccounts = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await accountService.getVisibleAccounts();
        
        const list = accountsArrayFromResponse(response);
        if (list.length > 0 || response.success) {
          const transformedMethods = buildCheckoutMethods(list);
          setPaymentMethods(transformedMethods);

          // Auto-select first payment method if none selected (only on initial load)
          if (transformedMethods.length > 0) {
            const codMethod = transformedMethods.find(m => m.type === 'cash-on-delivery');
            const defaultMethod = codMethod ? codMethod.type : transformedMethods[0].type;
            setSelectedPaymentMethod((prev) => {
              if (prev === 'safepay') return defaultMethod;
              if (prev && transformedMethods.some((m) => m.type === prev)) return prev;
              return defaultMethod;
            });
          }
        } else {
          setError('Failed to load payment methods');
          setPaymentMethods([]);
        }
      } catch (err) {
        console.error('Error fetching payment accounts:', err);
        setError(err.message || 'Failed to load payment methods');
        setPaymentMethods([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentAccounts();
  }, []); // Only fetch once on mount

  // Get only active payment methods
  const getActivePaymentMethods = () => {
    return paymentMethods.filter(method => method.isActive && method.isVisible);
  };

  // Get payment method by type (case-insensitive — backend may send mixed case)
  const getPaymentMethodByType = (type) => {
    const t = String(type || '').toLowerCase();
    return paymentMethods.find(
      (method) => String(method.type || '').toLowerCase() === t
    );
  };

  // Get selected payment method details
  const getSelectedPaymentMethod = () => {
    return getPaymentMethodByType(selectedPaymentMethod);
  };

  // Refresh payment methods from API
  const refreshPaymentMethods = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await accountService.getVisibleAccounts();
      
      const list = accountsArrayFromResponse(response);
      if (list.length > 0 || response.success) {
        const transformedMethods = buildCheckoutMethods(list);
        setPaymentMethods(transformedMethods);

        if (transformedMethods.length > 0) {
          const codMethod = transformedMethods.find((m) => m.type === 'cash-on-delivery');
          const defaultMethod = codMethod ? codMethod.type : transformedMethods[0].type;
          setSelectedPaymentMethod((prev) => {
            if (prev === 'safepay') return defaultMethod;
            if (prev && transformedMethods.some((m) => m.type === prev)) return prev;
            return defaultMethod;
          });
        }
      } else {
        setPaymentMethods([]);
      }
    } catch (err) {
      console.error('Error refreshing payment methods:', err);
      setError(err.message || 'Failed to refresh payment methods');
    } finally {
      setLoading(false);
    }
  };

  const refreshPaymentMethodsRef = useRef(refreshPaymentMethods);
  refreshPaymentMethodsRef.current = refreshPaymentMethods;

  useEffect(() => {
    const onAccountsUpdated = () => {
      refreshPaymentMethodsRef.current();
    };
    window.addEventListener('sheenwearPaymentAccountsUpdated', onAccountsUpdated);
    return () =>
      window.removeEventListener('sheenwearPaymentAccountsUpdated', onAccountsUpdated);
  }, []);

  const value = {
    paymentMethods,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    getActivePaymentMethods,
    getPaymentMethodByType,
    getSelectedPaymentMethod,
    refreshPaymentMethods,
    loading,
    error,
  };

  return (
    <PaymentContext.Provider value={value}>
      {children}
    </PaymentContext.Provider>
  );
};

export default PaymentContext; 